import { useState, useEffect, useRef, useCallback } from 'react';
import { Send, MessageCircle, Loader2, Mic, MicOff, Sparkles } from 'lucide-react';
import { useMessagingHub } from '../hooks/useSignalR';
import { createApiClient } from '../services/api';
import type { ChatMessage } from '../types';
import { UserAvatar } from './UserAvatar';

// Web Speech API resolve the vendor-prefixed constructor once 
type SpeechRecognitionCtor = new () => SpeechRecognition;
function resolveSpeechRecognition(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as Record<string, unknown>;
  const ctor = w['SpeechRecognition'] ?? w['webkitSpeechRecognition'] ?? null;
  return ctor as SpeechRecognitionCtor | null;
}
const SpeechRecognitionAPI = resolveSpeechRecognition();


interface MessagingPanelProps {
  jobId: string;
  jobReference: string;
  currentUserId: string;
  currentUserName?: string;
  getToken: () => Promise<string>;
  /**
   * When provided the panel filters to only messages exchanged between
   * `currentUserId` and `otherPartyId`, and targets that party when sending.
   * Used for the customer  specific-driver 1-1 thread.
   */
  otherPartyId?: string;
  otherPartyName?: string;
  /** Set to false to defer hub connection until MSAL has an account. Defaults to true. */
  enabled?: boolean;
  /** Quote message shown as the carrier's first message in the thread. */
  initialMessage?: string;
}

export function MessagingPanel({
  jobId,
  jobReference,
  currentUserId,
  currentUserName = 'Me',
  getToken,
  otherPartyId,
  otherPartyName = 'Carrier',
  enabled = true,
  initialMessage,
}: MessagingPanelProps) {
  const [messages,    setMessages]    = useState<(ChatMessage & { isMine: boolean })[]>([]);
  const [input,       setInput]       = useState('');
  const [sending,     setSending]     = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [recording,   setRecording]   = useState(false);
  const [aiImproving, setAiImproving] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const bottomRef      = useRef<HTMLDivElement>(null);

  // Thread filter: keep only messages in this 1-1 conversation 
  // When otherPartyId is set, keep any message that involves that driver 
  // avoids a dependency on currentUserId which may differ from the job's
  // stored CustomerId if the account was re-created or tested with a different login.
  const inThread = useCallback((msg: ChatMessage) => {
    if (!otherPartyId) return true;
    return msg.senderId === otherPartyId || msg.recipientId === otherPartyId;
  }, [otherPartyId]);

  // Load history 
  useEffect(() => {
    setLoading(true);
    createApiClient(getToken).get(`/api/messages/job/${jobId}`)
      .then(r => {
        const all: ChatMessage[] = r.data;
        setMessages(
          all
            .filter(inThread)
            // isMine: trust backend flag; fall back to "not sent by the other party"
            // so display is correct even if currentUserId differs from the stored CustomerId.
            .map(m => ({ ...m, isMine: m.isMine ?? m.senderId !== otherPartyId }))
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [jobId, inThread, getToken, otherPartyId]);

  // SignalR live messages 
  const onNew = useCallback((msg: ChatMessage) => {
    if (!inThread(msg)) return;
    const enriched = { ...msg, isMine: msg.isMine ?? msg.senderId !== otherPartyId };
    setMessages(prev => prev.find(m => m.id === enriched.id) ? prev : [...prev, enriched]);
  }, [inThread, otherPartyId]);

  useMessagingHub({ getToken, jobId, onMessageReceived: onNew, enabled });

  // Polling fallback (catches messages when SignalR can't connect)
  // Only runs when enabled — avoids spurious network requests in test environments
  // and when the panel is intentionally deferred (e.g. pre-auth).
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      createApiClient(getToken).get(`/api/messages/job/${jobId}`)
        .then(r => {
          const all: ChatMessage[] = r.data;
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const incoming = all
              .filter(inThread)
              .map(m => ({ ...m, isMine: m.isMine ?? m.senderId !== otherPartyId }))
              .filter(m => !existingIds.has(m.id));
            return incoming.length ? [...prev, ...incoming] : prev;
          });
        })
        .catch(() => { /* silent — SignalR still primary */ });
    }, 9_000);
    return () => clearInterval(id);
  }, [jobId, inThread, otherPartyId, getToken, enabled]);

  //  Auto-scroll 
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  //  Stop recognition when the panel unmounts 
  useEffect(() => {
    return () => { recognitionRef.current?.abort(); };
  }, []);

  //  Send 
  const handleSend = async () => {
    const content = input.trim();
    if (!content || sending) return;
    setSending(true);
    setInput('');
    try {
      const body: Record<string, unknown> = { jobId, content };
      if (otherPartyId) body.carrierId = otherPartyId;
      const r = await createApiClient(getToken).post('/api/messages', body);
      // Upsert: if SignalR already delivered the message (isMine=false), flip it to isMine=true
      // rather than appending a duplicate.
      setMessages(prev => {
        const exists = prev.find(m => m.id === r.data.id);
        if (exists) return prev.map(m => m.id === r.data.id ? { ...m, isMine: true } : m);
        return [...prev, { ...r.data, isMine: true }];
      });
    } catch { setInput(content); }
    finally { setSending(false); }
  };

  //  AI Improve 
  const handleAiImprove = async () => {
    const draft = input.trim();
    if (!draft || aiImproving) return;
    setAiImproving(true);
    try {
      const { data } = await createApiClient(getToken).post<{ message: string }>('/api/messages/improve', { message: draft });
      setInput(data.message);
    } catch { /* silent  user keeps their original draft */ }
    finally { setAiImproving(false); }
  };

  //  Voice-to-text 
  const toggleVoice = () => {
    if (!SpeechRecognitionAPI) return;

    if (recording) {
      recognitionRef.current?.stop();
      setRecording(false);
      return;
    }

    const rec = new SpeechRecognitionAPI();
    rec.lang = 'en-GB';
    rec.continuous = false;
    rec.interimResults = true;

    // Track the last interim span so we can replace it with the final result
    let interimStart = -1;

    rec.onstart = () => setRecording(true);

    rec.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      let final   = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += t;
        else interim += t;
      }
      setInput(prev => {
        // Replace any interim text appended in the last tick
        const base = interimStart >= 0 ? prev.slice(0, interimStart) : prev;
        const appended = base + (interim || final);
        if (final) interimStart = -1;
        else interimStart = base.length;
        return appended;
      });
    };

    rec.onerror  = () => setRecording(false);
    rec.onend    = () => setRecording(false);

    recognitionRef.current = rec;
    rec.start();
  };

  // 
  const title = otherPartyName
    ? `Chat with ${otherPartyName}`
    : `Messages Â· Job ${jobReference}`;

  const voiceSupported = SpeechRecognitionAPI !== null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl flex flex-col h-80">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 shrink-0">
        <MessageCircle className="w-4 h-4 text-blue-600" />
        <span className="font-medium text-gray-900 text-sm">{title}</span>
        {voiceSupported && (
          <span className="ml-1 text-xs text-gray-400" title="Voice-to-text available"></span>
        )}
        <div className="ml-auto w-2 h-2 bg-green-500 rounded-full" title="Live" />
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {initialMessage && (
              <div className="flex items-end gap-2 flex-row">
                <UserAvatar name={otherPartyName} role="Carrier" size="xs" className="mb-4 shrink-0" />
                <div className="flex flex-col max-w-[72%]">
                  <div className="rounded-2xl px-3.5 py-2 text-sm leading-relaxed bg-gray-100 text-gray-900 rounded-tl-sm">
                    {initialMessage}
                  </div>
                  <span className="text-xs text-gray-400 mt-1 px-1 text-left">Quote message</span>
                </div>
              </div>
            )}
            {messages.length === 0 && !initialMessage && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <MessageCircle className="w-8 h-8 text-gray-200 mb-2" />
                <p className="text-sm text-gray-400">No messages yet. Ask a question!</p>
              </div>
            )}
            {messages.map(msg => (
              <div key={msg.id} className={`flex items-end gap-2 ${msg.isMine ? 'flex-row-reverse' : 'flex-row'}`}>
                <UserAvatar
                  name={msg.isMine ? currentUserName : otherPartyName}
                  role={msg.isMine ? undefined : 'Carrier'}
                  size="xs"
                  className="mb-4 shrink-0"
                />
                <div className="flex flex-col max-w-[72%]">
                  <div className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed
                    ${msg.isMine
                      ? 'bg-blue-600 text-white rounded-tr-sm'
                      : 'bg-gray-100 text-gray-900 rounded-tl-sm'}`}>
                    {msg.content}
                  </div>
                  <span className={`text-xs text-gray-400 mt-1 px-1 ${msg.isMine ? 'text-right' : 'text-left'}`}>
                    {new Date(msg.sentAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    {msg.isMine && <span className="ml-1">{msg.isRead ? '✓✓' : '✓'}</span>}
                  </span>
                </div>
              </div>
            ))}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input row */}
      <div className="px-4 py-3 border-t border-gray-100 shrink-0">
        {recording && (
          <p className="text-xs text-red-500 mb-1.5 flex items-center gap-1">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            Listening speak now
          </p>
        )}
        {input.trim() && (
          <div className="mb-1.5">
            <button
              onClick={handleAiImprove}
              disabled={aiImproving}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold transition-colors"
            >
              {aiImproving
                ? <span className="w-3 h-3 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                : <Sparkles className="w-3 h-3" />
              }
              AI Improve
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            rows={2}
            placeholder={voiceSupported ? 'Type or tap  to speak' : 'Type a message (Enter to send)'}
            className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none placeholder-gray-400"
          />

          {/* Voice button  only shown when browser supports Speech API */}
          {voiceSupported && (
            <button
              onClick={toggleVoice}
              title={recording ? 'Stop recording' : 'Speak your message'}
              className={`p-2.5 rounded-lg border transition-colors shrink-0 ${
                recording
                  ? 'bg-red-500 border-red-500 text-white hover:bg-red-600 animate-pulse'
                  : 'border-gray-300 text-gray-500 hover:bg-gray-50'
              }`}
            >
              {recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          )}

          {/* Send button */}
          <button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300
                       text-white p-2.5 rounded-lg transition-colors shrink-0">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

